'use client';

import { Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import type { DiscountCodeResponse } from '@sharpr/types';
import { PricingPage } from './PricingPage';
import { TrackReferralVisit } from '@/components/posthog/TrackReferralVisit';

/**
 * Fires `referral_link_visited` only after `?ref=CODE` validates against the public
 * referral-code endpoint (botid-protected) — invalid / typoed codes never pollute the
 * funnel denominator. This used to be a server-side `getReferrerByCode()` lookup in
 * pricing/page.tsx; moved client-side so the page can be statically prerendered
 * (docs/seo-audit-2026-06-11.md H4). PricingPage's own useReferralCode hook fetches the
 * same endpoint for discount display — one extra lightweight GET, only on ?ref visits.
 */
function ValidatedReferralVisit({ code }: { code: string }) {
  const [validatedCode, setValidatedCode] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/billing/referral-code?code=${encodeURIComponent(code)}`)
      .then((res) => (res.ok ? (res.json() as Promise<DiscountCodeResponse>) : null))
      .then((json) => {
        // codeType guard: the endpoint resolves promo codes too, but those carry no
        // attribution — only true referrer codes belong in the referral funnel.
        if (!cancelled && json?.valid && json.codeType === 'referral') setValidatedCode(json.code);
      })
      .catch(() => {
        // Transient failure — skip the event rather than record an unvalidated code.
      });
    return () => {
      cancelled = true;
    };
  }, [code]);
  return validatedCode ? (
    <TrackReferralVisit code={validatedCode} sourcePage="pricing" />
  ) : null;
}

function PricingWithSearchParams() {
  const searchParams = useSearchParams();
  const ref = searchParams.get('ref') ?? undefined;
  return (
    <>
      <PricingPage
        checkoutCanceled={searchParams.get('canceled') === 'true'}
        referralCode={ref}
      />
      {ref && !ref.startsWith('share-') && <ValidatedReferralVisit code={ref} />}
    </>
  );
}

/**
 * Suspense boundary required by useSearchParams under static prerendering. The
 * fallback renders the param-less pricing UI so the prerendered HTML still carries
 * the full page content for crawlers; on hydration the params resolve synchronously
 * and the param-aware tree replaces it before any effects run.
 */
export function PricingPageClient() {
  return (
    <Suspense fallback={<PricingPage />}>
      <PricingWithSearchParams />
    </Suspense>
  );
}
